import React from "react";
import { Button } from "../../../components/ui";
import {
  dishMacros,
  formatMacro,
  isDishComplete,
  optionMacros,
  unitLabel,
} from "../planMapping";
import {
  MACRO_COLUMNS,
  MacroHeading,
  OptionColgroup,
  optionCardClasses,
  optionHeadCell,
  optionRuleClasses,
  optionTableClasses,
  optionTextCell,
} from "./optionTable";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
import type { MealOption } from "../types/plans.types";

// Read as a list, the columns are tabulated rather than merely labelled, so
// the header carries a rule — the form does not, because a row of boxes is
// already separated from its headings by its own borders. Nothing between the
// dishes themselves: a rule per row makes three dishes look like three tables.
const headCell = `${optionHeadCell} border-b-[0.5px] ${optionRuleClasses}`;

export interface MealOptionSummaryProps {
  option: MealOption;
  /**
   * Left off for a plan that is only being read — an active one. The foot row
   * stays either way, since it is what the totals sit in.
   */
  onModify?: () => void;
  /**
   * Whether the workspace may write this plan at all — see
   * `useSubscriptionGate`. Modify is the way into writing an option, so it is
   * where the answer belongs. Unlike an active plan the button is still drawn:
   * a lapsed subscription is something that can be put right, and a button that
   * had vanished would leave nowhere to say so.
   */
  gate?: GateProps;
}

/**
 * An option as it is read: the dishes in it as plain text, and what they come
 * to at the foot.
 *
 * This is what a plan opens on. A plan is looked at far more often than it is
 * written, and a screen of dropdowns is a poor way to read one — nothing on it
 * can be scanned, and every row invites a change nobody meant to make. Modify
 * swaps it for `MealOptionCard`, which is the same table with controls in it.
 *
 * Half-filled rows are left out. They count for nothing in the totals either,
 * and a row with no dish on it would read here as a blank line.
 *
 * Modify is the only action here, and an active plan is not given even that:
 * the option is what the client is already eating from. Deleting the option
 * belongs to the form that writes it — a card being read is not the place to
 * throw one away, and the bin sat close enough to Modify to be pressed for it.
 */
const MealOptionSummary: React.FC<MealOptionSummaryProps> = ({
  option,
  onModify,
  gate = { disabled: false },
}) => {
  const dishes = option.dishes.filter(isDishComplete);
  const totals = optionMacros(option);
  const instructions = option.instructions?.trim() ?? "";

  return (
    <div className={`overflow-hidden ${optionCardClasses}`}>
      <div className="overflow-x-auto custom-scrollbar-overlay px-4 pt-4">
        <table className={optionTableClasses}>
          <OptionColgroup />
          <thead>
            <tr>
              <th scope="col" className={headCell}>
                Name
              </th>
              <th scope="col" className={headCell}>
                Unit
              </th>
              <th scope="col" className={headCell}>
                Quantity
              </th>
              {MACRO_COLUMNS.map((macro) => (
                <th key={macro.key} scope="col" className={headCell}>
                  <MacroHeading macro={macro.key} label={macro.label} />
                </th>
              ))}
              <th scope="col" className={`${headCell} pr-0 3xl:pr-0`} />
            </tr>
          </thead>
          <tbody>
            {dishes.map((dish) => {
              const macros = dishMacros(dish);

              return (
                <tr key={dish.id}>
                  <td className={`${optionTextCell} pt-2 3xl:pt-3`}>
                    {/* Catalog names run long — "Afresh Energy Drink Mix
                        (Cinnamon Flavour)" — and one wrapped onto a second
                        line pushes its own macros away from the row they
                        belong to. Cut to the column instead, with the whole
                        of it on hover. */}
                    <span className="block truncate" title={dish.dish}>
                      {dish.dish}
                    </span>
                  </td>
                  <td className={`${optionTextCell} pt-2 3xl:pt-3`}>
                    {/* The unit is a name and a weight, so it runs long for
                        the same reason. */}
                    <span className="block truncate" title={unitLabel(dish)}>
                      {unitLabel(dish)}
                    </span>
                  </td>
                  <td className={`${optionTextCell} pt-2 3xl:pt-3`}>
                    {dish.quantity}
                  </td>
                  {MACRO_COLUMNS.map((macro) => (
                    <td
                      key={macro.key}
                      className={`${optionTextCell} pt-2 3xl:pt-3`}
                    >
                      {formatMacro(macros[macro.key])}
                    </td>
                  ))}
                  <td className={`${optionTextCell} pr-0 3xl:pr-0`} />
                </tr>
              );
            })}
          </tbody>
          {/* The totals sit in the table rather than under it, so each one
              lands in the column it is the total of. */}
          <tfoot>
            <tr className={`border-t-[0.5px] ${optionRuleClasses}`}>
              <td colSpan={3} className="py-3">
                {onModify && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onModify}
                    {...gate}
                  >
                    Modify
                  </Button>
                )}
              </td>
              {MACRO_COLUMNS.map((macro) => (
                <td
                  key={macro.key}
                  className={`${optionTextCell} pt-3 font-medium`}
                >
                  {formatMacro(totals[macro.key])}
                </td>
              ))}
              <td className={`${optionTextCell} pr-0 3xl:pr-0`} />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Tinted rather than set as another row: the table says what to eat,
          and this is the thing that would otherwise be missed — when, or what
          to keep away from it. Only drawn where there is one, the way a
          supplement's note is. */}
      {instructions && (
        <p className="mx-4 mb-4 rounded-[6px] border border-[#FFDDAF] bg-[#FFF5E2] px-3 py-2.5 text-[13px] 3xl:text-[14px] text-[#C78435]">
          <span className="font-medium">Instructions:</span> {instructions}
        </p>
      )}
    </div>
  );
};

export default MealOptionSummary;
